import { useMemo } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { WidgetField } from "@/domains/buildings/types/buildingWidget";
import { useAttributeHistory } from "@/hooks/buildings/useAttributeHistory";
import { getEnumRule, localizeEnumValue } from "./getEnumRule";
import { SourceGroupChip } from "./SourceGroupChip";
import { groupNonUserSources } from "./sourceGroups";
import {
  buildUserHistoryNodes,
  supportingGroupsForValue,
  type TimelineNodeKind,
} from "./buildHistoryTimeline";
import { HistoryTimelineItem } from "./HistoryTimelineItem";
import { formatHistoryDate } from "./formatHistoryDate";

/**
 * Per-attribute value-history modal (WHA-3720).
 *
 *  - **Timeline** — only user changes, newest-first, from the append-only
 *    `data.user_data` history (fetched lazily on open). Each node lists its
 *    "Supporting sources": the machine sources whose current value backs that edit.
 *  - **Franklin findings** — a bottom section listing *all* current machine
 *    findings (EHR / extractor / IFC) with their source chips.
 */

/** Dot / badge colour per source kind. */
const KIND_COLOR: Record<TimelineNodeKind, string> = {
  user: "hsl(155 63% 39%)", // confirmed-user edit
  ifc: "hsl(265 60% 55%)", // BIM violet
  ehr: "hsl(212 80% 50%)", // registry blue
  extractor: "hsl(38 92% 50%)", // extraction amber
};

interface AttributeHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  field: WidgetField;
  buildingId: string | undefined;
  orgId: string | undefined;
  registryCode?: string | null;
  onDocPreview: (filename: string) => void;
}

interface AttributeHistoryContentProps
  extends Omit<AttributeHistoryModalProps, "open" | "onOpenChange"> {
  enabled: boolean;
}

function AttributeHistoryContentBody({
  field,
  buildingId,
  orgId,
  registryCode,
  onDocPreview,
  enabled,
}: AttributeHistoryContentProps) {
  const { t, language } = useLanguage();
  const tw = t.buildings.buildingDataWidget;

  const enumRule = getEnumRule(field.validation, language);
  const isMultiSelect = enumRule?.kind === "enum_multiselect";
  const localize = (raw: string) => localizeEnumValue(raw, enumRule);

  const {
    data: history,
    isLoading,
    isError,
  } = useAttributeHistory(buildingId, orgId, field.id, enabled);

  const userNodes = useMemo(() => buildUserHistoryNodes(history), [history]);
  const franklinGroups = useMemo(() => groupNonUserSources(field.sources), [field.sources]);

  const machineBadgeLabel: Record<Exclude<TimelineNodeKind, "user">, string> = {
    ehr: tw.sourceEhr,
    extractor: tw.sourceExtractor,
    ifc: tw.sourceIfc,
  };

  const showEmpty = !isLoading && userNodes.length === 0 && franklinGroups.length === 0;

  const renderValue = (raw: string) => (
    <>
      {localize(raw) || "—"}
      {field.unit ? (
        <span style={{ marginLeft: 4, fontSize: 11, color: "var(--bd-ink-3)" }}>{field.unit}</span>
      ) : null}
    </>
  );

  return (
    <>
      {isLoading && (
        <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 size={14} className="animate-spin" />
          {tw.loadingHistory}
        </div>
      )}

      {showEmpty && (
        <div className="py-6 text-center text-sm text-muted-foreground">{tw.noHistory}</div>
      )}

      {/* Timeline — user edits only. */}
      {userNodes.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {userNodes.map((node, idx) => {
            const supporting = supportingGroupsForValue(field.sources, node.value, isMultiSelect);
            return (
              <HistoryTimelineItem
                key={node.key}
                dateText={formatHistoryDate(node.timestamp, language)}
                value={renderValue(node.value)}
                editor={{
                  id: node.editorId,
                  name: node.editorName,
                  avatarUrl: node.editorAvatarUrl,
                  color: node.editorColor,
                }}
                labels={{ editedBy: tw.editedBy, unknownUser: tw.unknownUser }}
                isLast={idx === userNodes.length - 1}
              >
                {supporting.length > 0 && (
                  <div
                    style={{
                      marginTop: 6,
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                    }}
                  >
                    <div
                      style={{
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        fontSize: 10,
                        color: "var(--bd-ink-4)",
                      }}
                    >
                      {tw.supportingSources}
                    </div>
                    {supporting.map((g, i) => (
                      <div key={`${g.kind}-${i}`} style={{ display: "flex" }}>
                        <SourceGroupChip
                          group={g}
                          registryCode={registryCode}
                          onDocPreview={onDocPreview}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </HistoryTimelineItem>
            );
          })}
        </div>
      )}

      {/* Franklin findings — all current machine findings. */}
      {franklinGroups.length > 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            paddingTop: userNodes.length > 0 ? 16 : 0,
          }}
        >
          <div style={{ height: 1, background: "var(--bd-line)" }} />
          <div
            style={{
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontSize: 11,
              fontWeight: 600,
              color: "var(--bd-ink-3)",
            }}
          >
            {tw.franklinFindings}
          </div>
          {franklinGroups.map((g, idx) => (
            <div
              key={`${g.kind}-${idx}`}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span
                  style={{
                    fontSize: 10,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    fontWeight: 600,
                    color: KIND_COLOR[g.kind],
                  }}
                >
                  {machineBadgeLabel[g.kind]}
                </span>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: "var(--bd-ink)",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                >
                  {renderValue(g.value)}
                </span>
              </div>
              <div style={{ display: "flex" }}>
                <SourceGroupChip
                  group={g}
                  registryCode={registryCode}
                  onDocPreview={onDocPreview}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {isError && !isLoading && (
        <div className="py-2 text-center text-xs text-muted-foreground">{tw.saveError}</div>
      )}
    </>
  );
}

export function AttributeHistoryContent(props: AttributeHistoryContentProps) {
  return <AttributeHistoryContentBody {...props} />;
}

export function AttributeHistoryModal({
  open,
  onOpenChange,
  field,
  buildingId,
  orgId,
  registryCode,
  onDocPreview,
}: AttributeHistoryModalProps) {
  const { t } = useLanguage();
  const tw = t.buildings.buildingDataWidget;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="building-data-widget max-h-[80vh] max-w-md overflow-y-auto bg-background">
        <DialogHeader>
          <DialogTitle>{field.label}</DialogTitle>
          <DialogDescription>{tw.valueHistory}</DialogDescription>
        </DialogHeader>
        <AttributeHistoryContent
          field={field}
          buildingId={buildingId}
          orgId={orgId}
          registryCode={registryCode}
          onDocPreview={onDocPreview}
          enabled={open}
        />
      </DialogContent>
    </Dialog>
  );
}
